/**
 * Integrations Hooks
 * Jotai atoms and hooks for integration management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { integrationService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';

// Query atoms
const integrationsQueryAtom = atomWithQuery((get) => ({
  // The brand is in the key as well as the params: two brands must never read
  // each other's cached connections, and a Connect click acts on what is shown.
  queryKey: ['integrations', 'account', buScopeKey(get)],
  queryFn: () => integrationService.getAccountIntegrations(buScopeParams(get)),
  refetchOnWindowFocus: true,
  staleTime: 30 * 1000
}));

// Mutation atoms
const disconnectIntegrationMutationAtom = atomWithMutation(() => ({
  mutationKey: ['integrations', 'disconnect'],
  mutationFn: (id: string) => integrationService.disconnectIntegration(id),
  onSuccess: () => {
    invalidateQueries(['integrations']);
  }
}));

const disconnectGorgiasMutationAtom = atomWithMutation(() => ({
  mutationKey: ['integrations', 'disconnect', 'gorgias'],
  mutationFn: (accountId: number) => integrationService.disconnectGorgias(accountId),
  onSuccess: () => {
    invalidateQueries(['integrations']);
  }
}));

const setPrimaryShopifyStoreMutationAtom = atomWithMutation(() => ({
  mutationKey: ['integrations', 'shopify', 'primary'],
  mutationFn: (id: string) => integrationService.setPrimaryShopifyStore(id),
  onSuccess: () => {
    invalidateQueries(['integrations']);
  }
}));

export const disconnectHelpScoutMutationAtom = atomWithMutation(() => ({
	mutationKey: ['integrations', 'disconnect', 'helpscout'],
	mutationFn: integrationService.disconnectHelpScout,
	onSuccess: () => {
		invalidateQueries(['integrations']);
	}
}));

// Hooks
export function useIntegrations() {
  const [integrationsQuery] = useAtom(integrationsQueryAtom);
  return integrationsQuery;
}

export function useDisconnectIntegration() {
  const [disconnectMutation] = useAtom(disconnectIntegrationMutationAtom);
  return disconnectMutation;
}

export function useSetPrimaryShopifyStore() {
  const [mutation] = useAtom(setPrimaryShopifyStoreMutationAtom);
  return mutation;
}

export function useDisconnectGorgias() {
  const [disconnectMutation] = useAtom(disconnectGorgiasMutationAtom);
  return disconnectMutation;
}

export function useDisconnectHelpScout() {
	const [disconnectMutation] = useAtom(disconnectHelpScoutMutationAtom);
	return disconnectMutation;
}

